/** Pista de la última sesión real del mismo día de plan (gym o deporte). */

export function fechaKey(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s.slice(0, 10);
}

function pickSetMap(map, setIndex) {
  if (!map || typeof map !== 'object') return '';
  const v = map[setIndex] ?? map[String(setIndex)];
  if (v == null || v === '') return '';
  return String(v);
}

function slashParts(raw) {
  if (raw == null || raw === '') return [];
  return String(raw)
    .split('/')
    .map((p) => p.trim())
    .filter(Boolean);
}

function pickSlash(raw, setIndex) {
  const parts = slashParts(raw);
  if (!parts.length) return '';
  return parts[setIndex] || '';
}

/**
 * @param {object} prevLog diario anterior del **mismo día de plan**
 * @param {string} exerciseName
 * @param {number} exerciseIndex posición en ese día (no buscar homónimos de otro slot/día)
 * @param {number} setIndex
 * @param {{ dia?: number }} [opts]
 * @returns {{ reps: string, kg: string, rir: string, fecha: string } | null}
 */
export function previousSetHint(prevLog, exerciseName, exerciseIndex, setIndex, opts = {}) {
  if (
    opts.dia != null && prevLog?.dia != null
    && Number.isFinite(Number(prevLog.dia))
    && Number.isFinite(Number(opts.dia))
    && Number(prevLog.dia) !== Number(opts.dia)
  ) {
    return null;
  }
  const list = Array.isArray(prevLog?.ejercicios) ? prevLog.ejercicios : [];
  if (!list.length) return null;
  const name = String(exerciseName || '').trim().toLowerCase();
  const ex = list[exerciseIndex];
  if (!ex) return null;
  const slotName = String(ex.exercise_name || ex.name || '').trim().toLowerCase();
  if (name && slotName && slotName !== name) return null;
  const reps = pickSetMap(ex.set_reps || ex.setReps, setIndex)
    || pickSlash(ex.reps_done || ex.reps, setIndex);
  let kg = pickSetMap(ex.set_weights || ex.setWeights, setIndex)
    || pickSlash(ex.weights_done, setIndex);
  if (!kg && Number(ex.weight_kg) > 0) {
    const slashReps = slashParts(ex.reps_done || ex.reps);
    if (slashReps.length) {
      if (slashReps[setIndex]) kg = String(ex.weight_kg);
    } else if (setIndex === 0) {
      kg = String(ex.weight_kg);
    }
  }
  const rir = pickSetMap(ex.set_intensities || ex.setIntensities, setIndex)
    || pickSlash(ex.intensity_actual, setIndex);
  if (!reps && !kg && !rir) return null;
  return {
    reps,
    kg,
    rir,
    fecha: fechaKey(prevLog.fecha),
  };
}

export function formatPrevWeekLabel(fecha) {
  const key = fechaKey(fecha);
  if (!key) return 'Última sesión';
  const d = new Date(`${key}T12:00:00`);
  if (!Number.isFinite(d.getTime())) return `Última sesión (${key})`;
  const label = new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short' }).format(d);
  return `Última sesión (${label})`;
}

/**
 * Resumen de Running/Natación de la última sesión del mismo día de plan.
 * @returns {{ fecha: string, lines: string[] } | null}
 */
export function previousSportHint(prevLog) {
  const sl = prevLog?.sport_log;
  if (!sl || typeof sl !== 'object') return null;
  const fecha = fechaKey(prevLog.fecha);
  const lines = [];
  const dur = Number(sl.duration_sec) || 0;
  if (dur > 0) {
    const h = Math.floor(dur / 3600);
    const m = Math.floor((dur % 3600) / 60);
    const s = dur % 60;
    lines.push(h > 0
      ? `tiempo ${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`
      : `tiempo ${m}m ${String(s).padStart(2, '0')}s`);
  }
  if (sl.distance_m != null && Number(sl.distance_m) > 0) {
    const km = Math.round((Number(sl.distance_m) / 1000) * 100) / 100;
    lines.push(km >= 1 ? `${km} km` : `${Math.round(Number(sl.distance_m))} m`);
  }
  if (Number(sl.pace_sec_per_km) > 0) {
    const p = Math.round(Number(sl.pace_sec_per_km));
    const mm = Math.floor(p / 60);
    const ss = p % 60;
    lines.push(`pace ${mm}:${String(ss).padStart(2, '0')} /km`);
  }
  if (Number(sl.pace_per_100_sec) > 0) {
    lines.push(`ritmo ${Number(sl.pace_per_100_sec)} s/100 m`);
  }
  if (sl.hr_avg_ppm != null && String(sl.hr_avg_ppm).trim() !== '') {
    lines.push(`FC ${sl.hr_avg_ppm} ppm`);
  }
  if (sl.zone != null && String(sl.zone).trim() !== '') {
    lines.push(`zona ${sl.zone}`);
  }
  if (sl.calories != null && Number(sl.calories) > 0) {
    lines.push(`${sl.calories} kcal`);
  }
  if (String(sl.notes || '').trim()) {
    lines.push(String(sl.notes).trim());
  }
  if (!fecha || !lines.length) return null;
  return { fecha, lines };
}

/** ¿El diario tiene kilos/series o deporte real? (no basta el cronómetro). */
export function logHasComparableLoad(log) {
  if (!log) return false;
  const list = Array.isArray(log.ejercicios) ? log.ejercicios : [];
  for (const ex of list) {
    if (Number(ex?.sets_done) > 0) return true;
    if (String(ex?.reps_done || '').trim()) return true;
    if (Number(ex?.weight_kg) > 0) return true;
    if (String(ex?.weights_done || '').trim()) return true;
    if (String(ex?.intensity_actual || '').trim()) return true;
    if (String(ex?.notes || '').trim()) return true;
    if (ex?.set_reps && Object.values(ex.set_reps).some(Boolean)) return true;
    if (ex?.set_weights && Object.values(ex.set_weights).some(Boolean)) return true;
    if (ex?.set_intensities && Object.values(ex.set_intensities).some(Boolean)) return true;
  }
  const sl = log.sport_log;
  if (!sl || typeof sl !== 'object') return false;
  if (Number(sl.duration_sec) > 0) return true;
  if (sl.hr_avg_ppm != null && String(sl.hr_avg_ppm).trim() !== '') return true;
  if (sl.zone != null && String(sl.zone).trim() !== '') return true;
  if (sl.calories != null && Number(sl.calories) > 0) return true;
  if (sl.distance_m != null && Number(sl.distance_m) > 0) return true;
  if (Number(sl.pace_sec_per_km) > 0) return true;
  if (Number(sl.pace_per_100_sec) > 0) return true;
  if (String(sl.notes || '').trim()) return true;
  return false;
}

export function pickLastComparableLog(candidates, beforeFecha) {
  const current = fechaKey(beforeFecha);
  if (!current || !Array.isArray(candidates) || !candidates.length) return null;
  const pool = candidates
    .map((row) => ({ row, fk: fechaKey(row?.fecha) }))
    .filter((x) => x.fk && x.fk < current && logHasComparableLoad(x.row));
  if (!pool.length) return null;
  pool.sort((a, b) => {
    const byFecha = b.fk.localeCompare(a.fk);
    if (byFecha !== 0) return byFecha;
    return Number(b.row?.id || 0) - Number(a.row?.id || 0);
  });
  return pool[0].row;
}

/**
 * Semana lun–dom +, si un día de plan no está en esa semana,
 * la última sesión real de ese día (p. ej. día 2 de hace 13 días).
 */
export function mergeIsoWeekHistory(logs, weekFrom, weekTo) {
  const from = fechaKey(weekFrom);
  const to = fechaKey(weekTo);
  if (!from || !to) return Array.isArray(logs) ? [...logs] : [];
  const inWeek = [];
  for (const log of logs || []) {
    const f = fechaKey(log?.fecha);
    if (!f || f < from || f > to) continue;
    inWeek.push(log);
  }
  inWeek.sort((a, b) => {
    const byFecha = fechaKey(a.fecha).localeCompare(fechaKey(b.fecha));
    if (byFecha !== 0) return byFecha;
    return Number(a.dia) - Number(b.dia);
  });
  const diasInWeek = new Set(inWeek.map((l) => Number(l.dia)));
  const extras = [];
  const dias = [...new Set((logs || []).map((l) => Number(l.dia)).filter((n) => Number.isFinite(n)))];
  for (const dia of dias) {
    if (diasInWeek.has(dia)) continue;
    const prev = pickLastComparableLog(
      (logs || []).filter((l) => Number(l.dia) === dia),
      from,
    );
    if (prev) extras.push({ ...prev, history_ref_outside_week: true });
  }
  extras.sort((a, b) => fechaKey(b.fecha).localeCompare(fechaKey(a.fecha)));
  return [...inWeek, ...extras];
}
